import moment from "moment"

export const EXPOSURE_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
const CALENDAR_DAYS = EXPOSURE_DAYS

function createEmptyDays() {
	return EXPOSURE_DAYS.reduce((acc, day) => {
		acc[day] = 0
		return acc
	}, {})
}

function normalizeInventory(inventory = {}) {
	return EXPOSURE_DAYS.reduce((acc, day) => {
		acc[day] = Math.max(0, Number(inventory?.[day]) || 0)
		return acc
	}, {})
}

function getTotalNeedToBuy(exposure = {}) {
	return EXPOSURE_DAYS.reduce((total, day) => total + (exposure?.[day]?.needToBuy || 0), 0)
}

function getTimestampMoment(timestamp) {
	if (!timestamp?.seconds) return null
	const parsed = moment(timestamp.seconds, "X")
	return parsed.isValid() ? parsed : null
}

function getEarliestHotelCheckIn(hotels = []) {
	const sortedCheckIns = (Array.isArray(hotels) ? hotels : [])
		.map((hotel) => hotel?.check_in)
		.filter((checkIn) => checkIn?.seconds)
		.sort((a, b) => a.seconds - b.seconds)

	return sortedCheckIns[0] || null
}

export function getCheckInDatesString(booking = {}) {
	const hotelCheckInDates = (Array.isArray(booking?.hotels) ? booking.hotels : [])
		.map((hotel) => getTimestampMoment(hotel?.check_in))
		.filter(Boolean)
		.sort((a, b) => a.valueOf() - b.valueOf())
		.map((date) => date.format("ddd D MMM"))

	const uniqueHotelDates = [...new Set(hotelCheckInDates)]
	if (uniqueHotelDates.length > 0) return uniqueHotelDates.join(", ")

	const bookingCheckIn = getTimestampMoment(booking?.check_in)
	return bookingCheckIn ? bookingCheckIn.format("ddd D MMM") : ""
}

function getArrivalMoment(booking = {}) {
	return getTimestampMoment(booking?.check_in || getEarliestHotelCheckIn(booking?.hotels))
}

export function getDayDateMap(eventConfig = {}) {
	const weekStart = String(eventConfig?.week_start || "").trim()
	if (!moment(weekStart, "YYYY-MM-DD", true).isValid()) return {}

	const start = moment(weekStart, "YYYY-MM-DD")

	return CALENDAR_DAYS.reduce((acc, day, index) => {
		acc[day] = start.clone().add(index, "days").format("YYYY-MM-DD")
		return acc
	}, {})
}

function buildArrivalReason({ booking, targetDay, targetDate }) {
	const arrivalMoment = getArrivalMoment(booking)
	if (!arrivalMoment) return null

	if (targetDate) {
		if (arrivalMoment.format("YYYY-MM-DD") >= targetDate) {
			return {
				code: "arrives_after_day",
				message: "Arrives on or after the target date",
			}
		}

		return null
	}

	const arrivalDay = arrivalMoment.format("dddd").toLowerCase()
	const arrivalIndex = CALENDAR_DAYS.indexOf(arrivalDay)
	const targetIndex = CALENDAR_DAYS.indexOf(targetDay)

	if (arrivalIndex !== -1 && targetIndex !== -1 && arrivalIndex >= targetIndex && arrivalDay !== "sunday") {
		return {
			code: "arrives_after_day",
			message: "Arrives on or after the target date",
		}
	}

	return null
}

function buildRoundConflictReasons({ booking, targetDay, targetDate }) {
	const rounds = Array.isArray(booking?.round_tee_times) ? booking.round_tee_times : []

	return rounds
		.filter((round) => {
			const roundMoment = getTimestampMoment(round?.tee_time)
			if (!roundMoment) return false

			if (targetDate) {
				return roundMoment.format("YYYY-MM-DD") === targetDate
			}

			return roundMoment.format("dddd").toLowerCase() === targetDay
		})
		.map((round) => ({
			code: "has_round_conflict",
			message: `Already has ${round?.course_name || "a golf round"} on ${formatDayLabel(targetDay)}`,
		}))
}

export function getMoveValidation({ booking = {}, targetDay, eventConfig = {} }) {
	if (!EXPOSURE_DAYS.includes(targetDay)) {
		return { valid: false, reasons: [{ code: "invalid_day", message: "Invalid target day" }] }
	}

	const dayDateMap = getDayDateMap(eventConfig)
	const targetDate = dayDateMap[targetDay] || ""
	const reasons = []

	const arrivalReason = buildArrivalReason({ booking, targetDay, targetDate })
	if (arrivalReason) reasons.push(arrivalReason)

	buildRoundConflictReasons({ booking, targetDay, targetDate }).forEach((reason) => reasons.push(reason))

	return {
		valid: reasons.length === 0,
		reasons,
		targetDate,
	}
}

export function buildBookingDemand({ bookings = [], guests = [] }) {
	const bookingsById = (Array.isArray(bookings) ? bookings : []).reduce((acc, booking) => {
		if (booking?.bookingID) acc[booking.bookingID] = booking
		return acc
	}, {})

	const demandByBooking = {}

	;(Array.isArray(guests) ? guests : []).forEach((guest) => {
		const bookingID = guest?.bookingID
		if (!bookingID) return

		if (!demandByBooking[bookingID]) {
			const booking = bookingsById[bookingID] || {}
			demandByBooking[bookingID] = {
				bookingID,
				reference: booking.reference || guest.booking_reference || "-",
				client: booking.client || null,
				agent: booking.agent || null,
				check_in: booking.check_in || null,
				groupSize: Number(booking.group_size) || 0,
				hotels: Array.isArray(booking.hotels) ? booking.hotels : [],
				round_tee_times: Array.isArray(booking.round_tee_times) ? booking.round_tee_times : [],
				totalGuests: 0,
				guestIDs: [],
				days: createEmptyDays(),
			}
		}

		const entry = demandByBooking[bookingID]
		entry.totalGuests += 1
		entry.guestIDs.push(guest.guestID)

		EXPOSURE_DAYS.forEach((day) => {
			if (guest?.masters_days?.[day]) {
				entry.days[day] += 1
			}
		})
	})

	return Object.values(demandByBooking)
		.map((entry) => ({
			...entry,
			activeDays: EXPOSURE_DAYS.filter((day) => entry.days[day] > 0),
		}))
		.filter((entry) => entry.activeDays.length > 0)
		.sort((a, b) => String(a.reference || "").localeCompare(String(b.reference || "")))
}

export function buildDayExposure({ bookingDemand = [], inventory = {} }) {
	const normalizedInventory = normalizeInventory(inventory)
	const required = createEmptyDays()

	;(Array.isArray(bookingDemand) ? bookingDemand : []).forEach((booking) => {
		EXPOSURE_DAYS.forEach((day) => {
			required[day] += Number(booking?.days?.[day]) || 0
		})
	})

	return EXPOSURE_DAYS.reduce((acc, day) => {
		const available = normalizedInventory[day]
		const needed = required[day]
		const delta = available - needed

		acc[day] = {
			required: needed,
			available,
			delta,
			needToBuy: Math.max(0, needed - available),
			surplus: Math.max(0, available - needed),
		}

		return acc
	}, {})
}

export function summarizeExposure(exposure = {}) {
	return EXPOSURE_DAYS.reduce((summary, day) => {
		const dayData = exposure?.[day] || {}
		summary.required += Number(dayData.required) || 0
		summary.available += Number(dayData.available) || 0
		summary.needToBuy += Number(dayData.needToBuy) || 0
		summary.surplus += Number(dayData.surplus) || 0
		return summary
	}, {
		required: 0,
		available: 0,
		needToBuy: 0,
		surplus: 0,
	})
}

export function buildExposureMetrics({ displayedExposure = {}, baseExposure = {} }) {
	const weekPassesToBuy = Math.max(
		displayedExposure?.thursday?.needToBuy || 0,
		displayedExposure?.friday?.needToBuy || 0,
		displayedExposure?.saturday?.needToBuy || 0,
		displayedExposure?.sunday?.needToBuy || 0
	)
	const displayedSupplierBuyTotal =
		(displayedExposure?.monday?.needToBuy || 0) +
		(displayedExposure?.tuesday?.needToBuy || 0) +
		(displayedExposure?.wednesday?.needToBuy || 0) +
		weekPassesToBuy
	const baseWeekPassesToBuy = Math.max(
		baseExposure?.thursday?.needToBuy || 0,
		baseExposure?.friday?.needToBuy || 0,
		baseExposure?.saturday?.needToBuy || 0,
		baseExposure?.sunday?.needToBuy || 0
	)
	const baseSupplierBuyTotal =
		(baseExposure?.monday?.needToBuy || 0) +
		(baseExposure?.tuesday?.needToBuy || 0) +
		(baseExposure?.wednesday?.needToBuy || 0) +
		baseWeekPassesToBuy

	return [
		{
			id: "change-vs-live",
			label: "Change vs live position",
			value: baseSupplierBuyTotal - displayedSupplierBuyTotal,
			helper: `Live buy total: ${baseSupplierBuyTotal}`,
			tone: "neutral",
		},
		{
			id: "monday-to-buy",
			label: "Mondays to buy",
			value: displayedExposure?.monday?.needToBuy || 0,
			helper: "Separate supplier ticket",
			tone: (displayedExposure?.monday?.needToBuy || 0) > 0 ? "warning" : "success",
		},
		{
			id: "tuesday-to-buy",
			label: "Tuesdays to buy",
			value: displayedExposure?.tuesday?.needToBuy || 0,
			helper: "Separate supplier ticket",
			tone: (displayedExposure?.tuesday?.needToBuy || 0) > 0 ? "warning" : "success",
		},
		{
			id: "wednesday-to-buy",
			label: "Wednesdays to buy",
			value: displayedExposure?.wednesday?.needToBuy || 0,
			helper: "Separate supplier ticket",
			tone: (displayedExposure?.wednesday?.needToBuy || 0) > 0 ? "warning" : "success",
		},
		{
			id: "week-passes-to-buy",
			label: "Week passes to buy",
			value: weekPassesToBuy,
			helper: "Best Thu-Sun gap",
			tone: weekPassesToBuy > 0 ? "warning" : "success",
		},
	]
}

export function applyStagedMoves({ bookingDemand = [], stagedMoves = [] }) {
	const clonedDemand = (Array.isArray(bookingDemand) ? bookingDemand : []).map((booking) => ({
		...booking,
		guestIDs: Array.isArray(booking.guestIDs) ? [...booking.guestIDs] : [],
		hotels: Array.isArray(booking.hotels) ? [...booking.hotels] : [],
		round_tee_times: Array.isArray(booking.round_tee_times) ? [...booking.round_tee_times] : [],
		days: {
			...createEmptyDays(),
			...(booking.days || {}),
		},
		activeDays: Array.isArray(booking.activeDays) ? [...booking.activeDays] : [],
	}))

	;(Array.isArray(stagedMoves) ? stagedMoves : []).forEach((move) => {
		const booking = clonedDemand.find((entry) => entry.bookingID === move?.bookingID)
		const fromDay = move?.fromDay
		const toDay = move?.toDay

		if (!booking || !EXPOSURE_DAYS.includes(fromDay) || !EXPOSURE_DAYS.includes(toDay) || fromDay === toDay) return

		const movedGuests = Number(booking.days[fromDay]) || 0
		if (movedGuests <= 0) return

		booking.days[fromDay] = 0
		booking.days[toDay] = (Number(booking.days[toDay]) || 0) + movedGuests
		booking.activeDays = EXPOSURE_DAYS.filter((day) => booking.days[day] > 0)
	})

	return clonedDemand
}

export function buildMoveSuggestions({ bookingDemand = [], inventory = {}, eventConfig = {}, limit = 20 }) {
	const baseExposure = buildDayExposure({ bookingDemand, inventory })
	const baseTotalNeed = getTotalNeedToBuy(baseExposure)
	const sourceDays = EXPOSURE_DAYS.filter((day) => (baseExposure?.[day]?.needToBuy || 0) > 0)
	const targetDays = EXPOSURE_DAYS.filter((day) => (baseExposure?.[day]?.surplus || 0) > 0)

	const suggestions = []

	;(Array.isArray(bookingDemand) ? bookingDemand : []).forEach((booking) => {
		sourceDays.forEach((fromDay) => {
			const movedGuests = Number(booking?.days?.[fromDay]) || 0
			if (movedGuests <= 0) return

			targetDays.forEach((toDay) => {
				if (fromDay === toDay) return
				const validation = getMoveValidation({ booking, targetDay: toDay, eventConfig })
				if ((validation.reasons || []).some((reason) => reason.code === "arrives_after_day")) return

				const targetSurplus = baseExposure?.[toDay]?.surplus || 0
				if (movedGuests > targetSurplus) return

				const simulatedDemand = applyStagedMoves({
					bookingDemand,
					stagedMoves: [{ id: `${booking.bookingID}-${fromDay}-${toDay}`, bookingID: booking.bookingID, fromDay, toDay }],
				})
				const simulatedExposure = buildDayExposure({ bookingDemand: simulatedDemand, inventory })
				const improvement = baseTotalNeed - getTotalNeedToBuy(simulatedExposure)
				const remainingSourceNeed = simulatedExposure?.[fromDay]?.needToBuy || 0
				const unusedTargetCapacity = targetSurplus - movedGuests

				if (improvement <= 0) return

				suggestions.push({
					id: `${booking.bookingID}-${fromDay}-${toDay}`,
					bookingID: booking.bookingID,
					reference: booking.reference,
					clientName: booking?.client?.name || "-",
					checkInDatesString: getCheckInDatesString(booking),
					agentName: booking?.agent?.name || "-",
					groupSize: booking.groupSize || booking.totalGuests || 0,
					fromDay,
					toDay,
					movedGuests,
					improvement,
					remainingSourceNeed,
					unusedTargetCapacity,
					validation,
					before: {
						[fromDay]: baseExposure[fromDay],
						[toDay]: baseExposure[toDay],
					},
					after: {
						[fromDay]: simulatedExposure[fromDay],
						[toDay]: simulatedExposure[toDay],
					},
				})
			})
		})
	})

	return suggestions
		.sort((a, b) => {
			if (a.remainingSourceNeed !== b.remainingSourceNeed) return a.remainingSourceNeed - b.remainingSourceNeed
			if (b.improvement !== a.improvement) return b.improvement - a.improvement
			if (a.unusedTargetCapacity !== b.unusedTargetCapacity) return a.unusedTargetCapacity - b.unusedTargetCapacity
			if (a.movedGuests !== b.movedGuests) return a.movedGuests - b.movedGuests
			return String(a.reference || "").localeCompare(String(b.reference || ""))
		})
		.slice(0, limit)
}

export function formatDayLabel(day) {
	if (!day) return "-"
	return day.charAt(0).toUpperCase() + day.slice(1)
}
